import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useApp } from "../../context/AppContext";
import api, { fileUrl } from "../../lib/api";
import adminApi from "../../lib/adminApi";
import { ADMIN } from "../../lib/adminI18n";
import { DEFAULT_STORES, ORIENT_SECTIONS, sectionStoreName, productMeta, splitName } from "../../lib/catalog";
import { Loader2, Plus, Trash2, Upload, Save, Eye, EyeOff, ImagePlus } from "lucide-react";

export default function ProductsTab() {
  const { lang } = useApp();
  const a = ADMIN[lang];
  const [storesData, setStoresData] = useState({ custom: [], hidden: [] });
  const [sectionProducts, setSectionProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [query, setQuery] = useState("");
  const [sectionId, setSectionId] = useState("");
  const [form, setForm] = useState({ store_id: "", name: "", price: "", old_price: "", emoji: "", image_path: "", stock: "", status: "active" });

  const sectionFilters = useMemo(
    () => (storesData.custom || []).filter((s) => String(s.name || "").startsWith("Orient Livsmedel")),
    [storesData]
  );

  const storeOptions = useMemo(() => {
    const custom = (storesData.custom || [])
      .filter((s) => !String(s.name || "").startsWith("__"))
      .map((s) => ({ store_id: s.store_id, name: s.name, isCustom: true }));
    const sections = ORIENT_SECTIONS.map((s) => ({ store_id: `sec:${s.slug}`, name: `➕ ${s.sv}`, isCustom: true, section: s }));
    const defaults = DEFAULT_STORES.map((s) => ({ store_id: s.store_id, name: s.name, isCustom: false }));
    const map = new Map();
    [...sections, ...defaults, ...custom].forEach((s) => map.set(s.store_id, s));
    return Array.from(map.values());
  }, [storesData]);

  useEffect(() => {
    api.getStores().then((s) => setStoresData(s)).catch(() => toast.error(a.error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pick the first section once stores load.
  useEffect(() => {
    if (!sectionId && sectionFilters.length) setSectionId(sectionFilters[0].store_id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionFilters.length]);

  const reload = useCallback(async () => {
    if (!sectionId) { setSectionProducts([]); setLoading(false); return; }
    setLoading(true);
    try {
      const d = await adminApi.sectionProducts(sectionId);
      setSectionProducts((d?.products || []).filter((p) => !p.deleted));
    } catch { toast.error(a.error); }
    finally { setLoading(false); }
  }, [sectionId, a.error]);
  useEffect(() => { reload(); }, [reload]);

  const storeName = (id) => storeOptions.find((s) => s.store_id === id)?.name || id;

  const q = query.trim().toLowerCase();
  const visibleProducts = sectionProducts.filter((p) => !q || String(p.name || "").toLowerCase().includes(q));
  const shown = visibleProducts.slice(0, 400);

  const onUploadForm = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminApi.upload(file);
      const path = res?.path || res?.image_path || res?.url || "";
      setForm((f) => ({ ...f, image_path: path }));
      toast.success(a.done);
    } catch (err) { toast.error(err?.response?.data?.detail || a.error); }
    finally { setUploading(false); }
  };

  const create = async () => {
    if (!form.store_id || !form.name.trim() || form.price === "") { toast.error(a.error); return; }
    const chosen = storeOptions.find((s) => s.store_id === form.store_id);
    setSaving(true);
    try {
      let storeId = form.store_id;
      let isCustom = !!chosen?.isCustom;
      if (storeId.startsWith("sec:")) {
        const sec = chosen?.section || ORIENT_SECTIONS.find((s) => s.slug === storeId.slice(4));
        const nm = sectionStoreName(sec.sv);
        let st = (storesData.custom || []).find((s) => s.name === nm);
        if (!st) {
          const res = await adminApi.createStore({ name: nm, category: "matbutik", color: sec.color });
          st = res?.store || res;
          const fresh = await api.getStores();
          setStoresData(fresh);
        }
        storeId = st.store_id;
        isCustom = true;
      }
      await adminApi.createProduct({
        store_id: storeId,
        name: form.name.trim(),
        price: Number(form.price),
        old_price: form.old_price ? Number(form.old_price) : null,
        emoji: form.emoji || null,
        image_path: form.image_path || null,
        stock: form.stock === "" ? null : Number(form.stock),
        status: form.status,
        is_custom: isCustom,
      });
      toast.success(a.done);
      setForm({ store_id: form.store_id, name: "", price: "", old_price: "", emoji: "", image_path: "", stock: "", status: "active" });
      if (storeId === sectionId) reload(); else setSectionId(storeId);
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
    finally { setSaving(false); }
  };

  // In-place update via PATCH — keeps the product id, no delete+recreate.
  const patch = useCallback(async (p, body) => {
    const updated = await adminApi.updateProduct(p.id, body);
    setSectionProducts((list) => list.map((x) => (x.id === p.id ? { ...x, ...updated } : x)).filter((x) => !x.deleted));
  }, []);

  const remove = async (p) => {
    if (!window.confirm(a.confirmDelete)) return;
    try {
      await adminApi.deleteProduct({ store_id: p.store_id, name: p.name, is_custom: !!p.is_custom });
      setSectionProducts((list) => list.filter((x) => x.id !== p.id));
      toast.success(a.done);
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
  };

  return (
    <div data-testid="admin-products" className="grid gap-6 lg:grid-cols-2">
      <div className="rounded-2xl border border-border bg-card p-5 h-fit">
        <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Plus className="h-4 w-4" /> {a.createProduct}</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{a.store}</label>
            <select data-testid="admin-product-store" value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3">
              <option value="">—</option>
              {storeOptions.map((s) => <option key={s.store_id} value={s.store_id}>{s.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">{a.productName}</label>
            <input data-testid="admin-product-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-primary/40" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{a.price}</label>
              <input data-testid="admin-product-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3" />
            </div>
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{a.oldPrice}</label>
              <input data-testid="admin-product-oldprice" type="number" value={form.old_price} onChange={(e) => setForm({ ...form, old_price: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 items-end">
            <div>
              <label className="text-xs font-semibold text-muted-foreground">{a.emoji}</label>
              <input data-testid="admin-product-emoji" value={form.emoji} onChange={(e) => setForm({ ...form, emoji: e.target.value })} maxLength={4} className="mt-1 w-full h-11 rounded-xl border border-border bg-background px-3 text-center text-xl" />
            </div>
            <label className="h-11 rounded-xl border border-dashed border-border bg-background flex items-center justify-center gap-2 text-sm font-semibold cursor-pointer hover:bg-accent">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {uploading ? a.uploading : a.image}
              <input data-testid="admin-product-image" type="file" accept="image/*" className="hidden" onChange={onUploadForm} />
            </label>
          </div>
          {form.image_path && <img src={fileUrl(form.image_path)} alt="" className="h-20 rounded-lg object-cover" />}
          <button data-testid="admin-product-create" disabled={saving} onClick={create} className="w-full h-11 rounded-xl bg-primary text-primary-foreground font-semibold disabled:opacity-60 flex items-center justify-center gap-2">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : a.add}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card p-5">
        <h3 className="font-head font-bold mb-3">{a.allProducts} · {visibleProducts.length}</h3>
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          <select data-testid="admin-products-filter" value={sectionId} onChange={(e) => { setSectionId(e.target.value); setQuery(""); }} className="h-10 rounded-xl border border-border bg-background px-3 text-sm sm:w-1/2">
            {sectionFilters.length === 0 && <option value="">{a.selectSection}</option>}
            {sectionFilters.map((s) => (
              <option key={s.store_id} value={s.store_id}>{s.name}</option>
            ))}
          </select>
          <input data-testid="admin-products-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={a.searchPlaceholder} className="h-10 rounded-xl border border-border bg-background px-3 text-sm flex-1 outline-none focus:ring-2 focus:ring-primary/40" />
        </div>
        {loading ? (
          <div className="grid place-items-center py-10 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : visibleProducts.length === 0 ? (
          <div data-testid="admin-products-empty" className="text-sm text-muted-foreground py-8 text-center">{a.noProducts}</div>
        ) : (
          <>
            <div className="space-y-2 max-h-[32rem] overflow-y-auto no-scrollbar">
              {shown.map((p) => (
                <ProductRow key={p.id} p={p} a={a} storeName={storeName} onPatch={patch} onRemove={remove} />
              ))}
            </div>
            {visibleProducts.length > shown.length && (
              <p className="text-xs text-muted-foreground mt-3 text-center">{a.showingFirst} {shown.length} / {visibleProducts.length} — {a.refineHint}</p>
            )}
          </>
        )}
      </div>
    </div>
  );
}


function ProductRow({ p, a, storeName, onPatch, onRemove }) {
  const meta = productMeta(p);
  const nm = splitName(meta.clean);
  const [stock, setStock] = useState(meta.stock ?? "");
  const [price, setPrice] = useState(p.price ?? "");
  const [oldPrice, setOldPrice] = useState(p.old_price ?? "");
  const [busy, setBusy] = useState(false);
  const [imgBusy, setImgBusy] = useState(false);
  const [hideBusy, setHideBusy] = useState(false);

  const hidden = (meta.status || "active") !== "active";
  const dirty = String(price) !== String(p.price ?? "") || String(oldPrice) !== String(p.old_price ?? "") || String(stock) !== String(meta.stock ?? "");

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const res = await adminApi.upload(file);
      const path = res?.path || res?.image_path || res?.url || "";
      if (path) { await onPatch(p, { image_path: path }); toast.success(a.done); }
    } catch (err) { toast.error(err?.response?.data?.detail || a.error); }
    finally { setImgBusy(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      await onPatch(p, { price: price === "" ? p.price : Number(price), old_price: oldPrice === "" ? null : Number(oldPrice), stock: stock === "" ? null : Number(stock) });
      toast.success(a.done);
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
    finally { setBusy(false); }
  };

  const toggleHide = async () => {
    setHideBusy(true);
    try {
      await onPatch(p, { status: hidden ? "active" : "inactive" });
      toast.success(a.done);
    } catch (e) { toast.error(e?.response?.data?.detail || a.error); }
    finally { setHideBusy(false); }
  };

  return (
    <div data-testid={`admin-product-${p.id}`} className={`rounded-xl border p-2.5 ${hidden ? "border-border bg-muted/40 opacity-70" : "border-border"}`}>
      <div className="flex items-center gap-3">
        <label data-testid={`admin-product-img-${p.id}`} title={a.image} className={`relative h-12 w-12 rounded-lg grid place-items-center overflow-hidden shrink-0 text-lg cursor-pointer bg-accent ${p.image_path ? "" : "border-2 border-dashed border-primary/50"}`}>
          {p.image_path ? <img src={fileUrl(p.image_path)} alt="" className="h-full w-full object-cover" /> : <ImagePlus className="h-5 w-5 text-primary/70" />}
          <span className="absolute bottom-0 end-0 bg-primary text-primary-foreground rounded-tl-md p-0.5 leading-none">
            {imgBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
          </span>
          <input data-testid={`admin-product-img-input-${p.id}`} type="file" accept="image/*" className="hidden" onChange={onPickImage} />
        </label>
        <div className="min-w-0 flex-1">
          <div className="font-semibold truncate text-sm">{nm.sv || meta.clean}</div>
          <div className="text-xs text-muted-foreground">{storeName(p.store_id)} · {p.price} {a.kr}{p.old_price ? ` (${p.old_price})` : ""}</div>
        </div>
        {hidden && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full shrink-0 bg-muted text-muted-foreground">{a.hiddenBadge}</span>}
        <button data-testid={`admin-product-hide-${p.id}`} disabled={hideBusy} onClick={toggleHide} title={hidden ? a.showAction : a.hideAction} className={`h-9 px-2.5 rounded-lg text-sm font-semibold grid place-items-center shrink-0 disabled:opacity-50 ${hidden ? "bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20" : "bg-amber-500/10 text-amber-600 hover:bg-amber-500/20"}`}>
          {hideBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : hidden ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
        </button>
        <button data-testid={`admin-product-delete-${p.id}`} onClick={() => onRemove(p)} className="h-9 w-9 rounded-lg bg-red-500/10 text-red-600 grid place-items-center hover:bg-red-500/20 shrink-0"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <input data-testid={`admin-product-price-${p.id}`} type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder={a.price} title={a.price} className="w-20 h-9 rounded-lg border border-border bg-background px-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
        <input data-testid={`admin-product-oldprice-${p.id}`} type="number" value={oldPrice} onChange={(e) => setOldPrice(e.target.value)} placeholder={a.oldPrice} title={a.oldPrice} className="w-24 h-9 rounded-lg border border-border bg-background px-2.5 text-sm outline-none focus:ring-2 focus:ring-destructive/40" />
        <input data-testid={`admin-product-stock-${p.id}`} type="number" value={stock} onChange={(e) => setStock(e.target.value)} placeholder={a.unlimited} title={a.stock} className="w-24 h-9 rounded-lg border border-border bg-background px-2.5 text-sm outline-none focus:ring-2 focus:ring-primary/40" />
        <button data-testid={`admin-product-save-${p.id}`} disabled={!dirty || busy} onClick={save} className="h-9 px-3 rounded-lg bg-primary text-primary-foreground text-sm font-semibold disabled:opacity-40 flex items-center gap-1.5 ms-auto">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {a.save}
        </button>
      </div>
    </div>
  );
}
